import { Component, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import AOS from 'aos';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantTechgeekComponent } from '../relevant-techgeek/relevant-techgeek.component';
import { ResourcesService } from '../../../services/resources.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

interface ArticlePost {
  id: number;
  title: string;
  image: string;
  slug?: string;
}
@Component({
  selector: 'app-techgeek-2',
  imports: [CommonModule, RelevantTechgeekComponent, ReactiveFormsModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './techgeek-2.component.html',
  styleUrl: './techgeek-2.component.scss'
})
export class Techgeek2Component implements AfterViewInit, OnInit, OnDestroy {
  isModalOpen = false;
  isSubmitting = false;
  submitSuccess = false;
  errorMessage = '';
  downloadForm!: FormGroup;
  private jsonLdScript: any;

  readonly techGeekTitle = "Tech Geek Ed. 2";
  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }

  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {

      this.isSubmitting = true;
      this.errorMessage = '';

      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        TechGeekTitle: this.techGeekTitle
      };

      this.resourcesService.sendTechGeekEmail(payload).subscribe({
        next: (response: any) => {
          if (response && !response.IsError) {
            this.submitSuccess = true;
            this.isSubmitting = false;

            setTimeout(() => {
              this.closeModal();
            }, 4000);
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message;
          }
        },
        error: () => {
          this.isSubmitting = false;
          this.errorMessage = "Server error. Please try again.";
        }
      });
    }
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngAfterViewInit() {
    AOS.init();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const techGeek2Schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-2",
          "url": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-2",
          "name": "Tech Geek Ed. 2 — Kalpita Technologies Tech Magazine",
          "description": "Kalpita Tech Geek Magazine Edition 2 explores trends like secure P2P file sharing, Supercook, BLACKBOX AI, subtitle glasses for the deaf, Google Maps VPS, and AI emotion detection.",
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": [
              ".softwaredev-heading",
              ".takeaways-section .takeaway-item p",
              ".section-title"
            ]
          },
          "significantLink": ["https://heyzine.com/flip-book/30f82e921a.html"],
          "datePublished": "2023-01-01",
          "dateModified": "2023-03-31",
          "isPartOf": { "@type": "WebSite", "@id": "https://www.kalpitatechnologies.com/#website" }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-2#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Tech Geek", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek" },
            { "@type": "ListItem", "position": 3, "name": "Tech Geek Ed. 2", "item": "https://www.kalpitatechnologies.com/kalpita-tech-geek/techgeek-2" }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Tech Geek Ed. 2: Cybersecurity, AI Assistants, and Cloud Comparisons",
          "articleBody": "Kalpita Tech Geek Magazine Edition 2 is organized into App Inventions (Secure P2P, Supercook, BLACKBOX AI), R&D (Subtitle Glasses, Google Maps VPS, Emotion Detection), and a Cloud Computing comparison of AWS, Azure, and Google Cloud.",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "associatedMedia": {
            "@type": "MediaObject",
            "contentUrl": "https://heyzine.com/flip-book/30f82e921a.html",
            "name": "Tech Geek Ed. 2 — Digital Flipbook Edition"
          }
        },
        {
          "@type": "HowTo",
          "name": "How to Leverage Tech Trends from Tech Geek Ed. 2",
          "step": [
            { "@type": "HowToStep", "name": "Secure P2P Adoption", "text": "Evaluate Netherlands-based P2P services that prioritize GDPR-aligned data privacy." },
            { "@type": "HowToStep", "name": "Integrate AI Tools", "text": "Adopt BLACKBOX AI to debug 10x faster and Supercook for smarter personal productivity." },
            { "@type": "HowToStep", "name": "Use AR Navigation", "text": "Enable Google Maps VPS for more accurate real-world positioning via smartphone cameras." },
            { "@type": "HowToStep", "name": "Evaluate Cloud Options", "text": "Compare AWS for market leadership versus Azure for enterprise-native integration." }
          ]
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What is BLACKBOX AI?",
              "acceptedAnswer": { "@type": "Answer", "text": "BLACKBOX AI is an assistant designed to help developers code 10x faster through automated debugging and syntax detection." }
            },
            {
              "@type": "Question",
              "name": "How do subtitle glasses work?",
              "acceptedAnswer": { "@type": "Answer", "text": "These smart glasses sync with devices to stream real-time subtitles directly into the lenses, aiding users with hearing loss." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        },
        {
          "@type": "LocalBusiness",
          "@id": "https://www.kalpitatechnologies.com/#localbusiness",
          "name": "Kalpita Technologies — Austin Office",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Suite #300, 3rd Floor, Building 2, 1250 South Capital of Texas Highway",
            "addressLocality": "Austin",
            "addressRegion": "TX",
            "postalCode": "78746",
            "addressCountry": "US"
          },
          "telephone": "+1-512-240-2484"
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(techGeek2Schema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }
  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  articles: ArticlePost[] = [
    {
      id: 1,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-one.webp'
    },
    {
      id: 2,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-two.webp'
    },
    {
      id: 3,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-three.webp'
    },
    {
      id: 4,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-four.webp'
    },
    {
      id: 5,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-five.webp'
    },
    {
      id: 6,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-six.webp'
    },
    {
      id: 7,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-seven.webp'
    },
    {
      id: 8,
      title: 'Lorem ipsum dolor sit amet, consectetur elit',
      image: '../../../../assets/resources/whitepaper/it-trends/art-card-eight.webp'
    }
  ];
  onArticleClick(article: ArticlePost): void {
    console.log('Article clicked:', article);
    // Handle navigation to article detail page
  }

  trackByArticle(index: number, article: ArticlePost): number {
    return article.id;
  }
}
